import { AnswerResponseProgress } from '@@types/answer.types';
import {
  AnswerAdditionalDataDtoTimeUnitEnum,
  AnswerNestedSelectDto,
  QuestionSelectDto,
  QuestionType,
  SurveySelectDto,
  SurveySnapshotChapterIdentifier,
  SurveySnapshotSectionSelectDto,
} from '@@types/api.types';
import { RadarChartComponent } from '@components/shared/radar-chart.component';
import { RadarModal } from '@components/shared/radar-modal.component';
import { Tile } from '@components/shared/tile.component';
import { ZoomInIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';

type TrlCharacteristicsTileProps = {
  isLoading?: boolean;
  survey: SurveySelectDto | null;
};

type TrlCharacteristicsDataPoint = {
  costEstimate: number;
  fullName: string;
  label: string;
  timeSpent: number;
  timeUnit?: AnswerAdditionalDataDtoTimeUnitEnum;
};

type TimeDisplayConfig = {
  label: string;
  needsNormalization: boolean;
};

// Conversion factors to normalize all units to days (work-day based)
const TIME_UNIT_TO_DAYS: Record<AnswerAdditionalDataDtoTimeUnitEnum, number> = {
  [AnswerAdditionalDataDtoTimeUnitEnum.DAY]: 1,
  [AnswerAdditionalDataDtoTimeUnitEnum.HOUR]: 0.125, // 1/8 of a work day
  [AnswerAdditionalDataDtoTimeUnitEnum.MONTH]: 22, // ~4.4 work weeks
  [AnswerAdditionalDataDtoTimeUnitEnum.WEEK]: 5, // 5 work days
  [AnswerAdditionalDataDtoTimeUnitEnum.YEAR]: 260, // 52 weeks * 5 days
};

const RADAR_SEGMENTS = 5;

// Map full characteristic labels to short one-word labels for radar chart display
const SHORT_LABEL_MAP: Record<string, string> = {
  Compliance: 'Compliance',
  Concept: 'Concept',
  Demonstration: 'Demonstration',
  Engineering: 'Engineering',
  Integration: 'Integration',
  Reliability: 'Reliability',
  Risk: 'Risk',
  Validation: 'Validation',
};

function getShortLabel(fullLabel: string): string {
  // Check if it's already a short label
  if (SHORT_LABEL_MAP[fullLabel]) {
    return fullLabel;
  }

  // Try to find a matching short label by checking if any key is contained in the full label
  for (const [key] of Object.entries(SHORT_LABEL_MAP)) {
    if (fullLabel.toLowerCase().includes(key.toLowerCase())) {
      return key;
    }
  }

  // Fallback: return first word of the label
  return fullLabel.split(' ')[0];
}

function getTimeDisplayConfig(dataPoints: TrlCharacteristicsDataPoint[]): TimeDisplayConfig {
  const timeUnits = dataPoints
    .map((d) => d.timeUnit)
    .filter((unit): unit is AnswerAdditionalDataDtoTimeUnitEnum => unit !== undefined);
  const uniqueUnits = new Set(timeUnits);

  // If only one unit type, use it directly without normalization
  if (uniqueUnits.size === 1) {
    const unit = timeUnits[0];
    const labelMap: Record<AnswerAdditionalDataDtoTimeUnitEnum, string> = {
      [AnswerAdditionalDataDtoTimeUnitEnum.DAY]: 'days',
      [AnswerAdditionalDataDtoTimeUnitEnum.HOUR]: 'hours',
      [AnswerAdditionalDataDtoTimeUnitEnum.MONTH]: 'months',
      [AnswerAdditionalDataDtoTimeUnitEnum.WEEK]: 'weeks',
      [AnswerAdditionalDataDtoTimeUnitEnum.YEAR]: 'years',
    };
    return { label: labelMap[unit], needsNormalization: false };
  }

  // Multiple units - normalize to days
  return { label: 'days', needsNormalization: true };
}

function getTimeValue(params: { dataPoint: TrlCharacteristicsDataPoint; needsNormalization: boolean }): number {
  const { dataPoint, needsNormalization } = params;
  const { timeSpent, timeUnit } = dataPoint;

  if (timeSpent === undefined || timeSpent === null) {
    return 0;
  }

  if (!needsNormalization) {
    return timeSpent;
  }

  return timeSpent * (TIME_UNIT_TO_DAYS[timeUnit!] ?? 1);
}

function buildTrlCharacteristicsData(params: {
  answers: AnswerNestedSelectDto[];
  questions: QuestionSelectDto[];
  section: SurveySnapshotSectionSelectDto;
}): TrlCharacteristicsDataPoint[] {
  const { answers, questions, section } = params;

  const dataPoints: TrlCharacteristicsDataPoint[] = [];

  for (const questionId of section.questions) {
    const question = questions.find((q) => q.id === questionId);
    if (!question || question.type !== QuestionType.TRL_CHARACTERISTICS) continue;

    const answer = answers.find((a) => a.questionId === questionId && a.sectionId === section.id);

    // Skip NOT_APPLICABLE answers - do not include in radar chart
    if (answer?.response === AnswerResponseProgress.NOT_APPLICABLE) continue;

    const additionalData = answer?.additionalData;

    const hasData =
      (additionalData?.costEstimate !== undefined && additionalData.costEstimate > 0) ||
      (additionalData?.timeSpent !== undefined && additionalData.timeSpent > 0);

    if (!hasData || !question.label) continue;

    dataPoints.push({
      costEstimate: additionalData?.costEstimate ?? 0,
      fullName: question.label,
      label: getShortLabel(question.label),
      timeSpent: additionalData?.timeSpent ?? 0,
      timeUnit: additionalData?.timeUnit,
    });
  }

  return dataPoints;
}

export const TrlCharacteristicsTile: FC<TrlCharacteristicsTileProps> = (props) => {
  const { isLoading, survey } = props;

  const [isOpenFullScreenModal, setIsOpenFullScreenModal] = useState(false);

  const trlCharacteristicsData = useMemo(() => {
    if (!survey?.snapshot?.chapters) return [];

    const mainQuestionsChapter = survey.snapshot.chapters.find(
      (chapter) => chapter.identifier === SurveySnapshotChapterIdentifier.MAIN_QUESTIONS,
    );

    if (!mainQuestionsChapter) return [];

    const characteristicsSection = mainQuestionsChapter.sections[0];

    if (!characteristicsSection) return [];

    return buildTrlCharacteristicsData({
      answers: survey.answers,
      questions: survey.questions,
      section: characteristicsSection,
    });
  }, [survey]);

  const timeDisplayConfig = useMemo(() => getTimeDisplayConfig(trlCharacteristicsData), [trlCharacteristicsData]);

  const { costMax, timeMax } = useMemo(() => {
    if (trlCharacteristicsData.length === 0) {
      return { costMax: 0, timeMax: 0 };
    }

    const timeValues = trlCharacteristicsData.map((d) =>
      getTimeValue({ dataPoint: d, needsNormalization: timeDisplayConfig.needsNormalization }),
    );

    return {
      costMax: Math.max(...trlCharacteristicsData.map((d) => d.costEstimate)),
      timeMax: Math.max(...timeValues),
    };
  }, [trlCharacteristicsData, timeDisplayConfig.needsNormalization]);

  const normalizedData = useMemo(() => {
    if (trlCharacteristicsData.length === 0 || (costMax === 0 && timeMax === 0)) {
      return [];
    }

    return trlCharacteristicsData.map((point) => {
      const timeValue = getTimeValue({
        dataPoint: point,
        needsNormalization: timeDisplayConfig.needsNormalization,
      });

      return {
        costActual: point.costEstimate,
        costNormalized: costMax > 0 ? (point.costEstimate / costMax) * RADAR_SEGMENTS : 0,
        fullName: point.fullName,
        label: point.label,
        timeActual: timeValue,
        timeNormalized: timeMax > 0 ? (timeValue / timeMax) * RADAR_SEGMENTS : 0,
      };
    });
  }, [trlCharacteristicsData, costMax, timeMax, timeDisplayConfig.needsNormalization]);

  const hasData = normalizedData.length > 0;

  const series = [
    {
      color: '#3b82f6',
      dataKey: 'timeNormalized',
      fillColor: '#93c5fd',
      label: 'Time',
      tooltipFormatter: ({ payload }) => {
        const value = payload.timeActual ?? 0;
        return `${Number(value.toFixed(1))} ${timeDisplayConfig.label}`;
      },
    },
    {
      color: '#22c55e',
      dataKey: 'costNormalized',
      fillColor: '#86efac',
      label: 'Cost (USD)',
      tooltipFormatter: ({ payload }) => {
        const value = payload.costActual ?? 0;
        return new Intl.NumberFormat('en-US', {
          currency: 'USD',
          maximumFractionDigits: 2,
          minimumFractionDigits: 0,
          style: 'currency',
        }).format(value);
      },
    },
  ];

  return (
    <>
      <Tile
        className="min-h-[300px]"
        emptyState={{
          title: 'TRL Characteristics',
          message: 'No Data',
        }}
        header={{
          iconButton: {
            icon: <ZoomInIcon />,
            onClick: () => setIsOpenFullScreenModal(true),
          },
          title: 'TRL Characteristics',
        }}
        hoverReveal
        isEmpty={!hasData}
        isLoading={isLoading}
      >
        {hasData && (
          <div className="flex flex-col items-center">
            <RadarChartComponent
              maxValue={RADAR_SEGMENTS}
              nodes={normalizedData}
              series={series}
            />
          </div>
        )}
      </Tile>
      {isOpenFullScreenModal && (
        <RadarModal
          isOpen={isOpenFullScreenModal}
          onClose={() => setIsOpenFullScreenModal(false)}
          title="TRL Characteristics"
        >
          {({ labelFontSize, size }) => (
            <RadarChartComponent
              labelFontSize={labelFontSize}
              layout="full-screen"
              maxValue={RADAR_SEGMENTS}
              nodes={normalizedData}
              series={series}
              size={size}
            />
          )}
        </RadarModal>
      )}
    </>
  );
};
